import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { createBridgeSession } from '@/lib/factory-auth';
import {
  BRIDGE_BASE_URL,
  BRIDGE_ERROR_CODE,
  authorizeBridge,
  isAbortError,
  isFactoryBridgeError,
  listBridgeDevices,
  startBridge,
  startProvision,
  waitForProvision,
} from '@/lib/factory-bridge';
import {
  PROVISION_FIELD_MAX,
  PROVISION_STAGES,
  isProvisionWriteStage,
  provisionFormSchema,
  provisionWriteStageIndex,
  selectableBridgeDevices,
  type BridgeDevice,
  type BridgeDeviceReason,
  type BridgeSession,
  type ProvisionFormValues,
  type ProvisionProgress,
  type ProvisionWriteStage,
} from '@/schemas/factory-usb';

/** Authorization → bridge reachability → medium choice and signatures → live issue progress. */
type WizardStep = 'auth' | 'bridge' | 'devices' | 'progress';

export interface FactoryUsbProvisionWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fired once the bridge reports `done`, so the registry can be refreshed. */
  onIssued?: (progress: ProvisionProgress) => void;
}
const STEP_KEYS: Record<WizardStep, string> = {
  auth: 'factoryUsb.wizardStepAuth',
  bridge: 'factoryUsb.wizardStepBridge',
  devices: 'factoryUsb.wizardStepDevice',
  progress: 'factoryUsb.wizardStepProgress',
};

const STEP_ORDER: readonly WizardStep[] = ['auth', 'bridge', 'devices', 'progress'];

/** Step the Back button of the footer returns to; the first step has none. */
const BACK_STEP: Partial<Record<WizardStep, WizardStep>> = {
  bridge: 'auth',
  devices: 'bridge',
};

const STAGE_LABEL_KEYS: Record<ProvisionWriteStage, string> = {
  detecting: 'factoryUsb.wizardStageDetecting',
  generating: 'factoryUsb.wizardStageGenerating',
  writing: 'factoryUsb.wizardStageWriting',
  verifying: 'factoryUsb.wizardStageVerifying',
  registering: 'factoryUsb.wizardStageRegistering',
};

const DEVICE_REASON_KEYS: Record<BridgeDeviceReason, string> = {
  notMounted: 'factoryUsb.wizardReasonNotMounted',
  noKeyFile: 'factoryUsb.wizardReasonNoKeyFile',
  noIdentifier: 'factoryUsb.wizardReasonNoIdentifier',
  ambiguous: 'factoryUsb.wizardReasonAmbiguous',
};

/** Bridge failures are shown as our own message, never as raw agent text. */
function messageKeyFor(error: unknown): string {
  if (isFactoryBridgeError(error)) {
    if (error.errorCode === BRIDGE_ERROR_CODE.unavailable)
      return 'factoryUsb.wizardBridgeUnavailable';
    if (error.errorCode === BRIDGE_ERROR_CODE.tokenConsumed)
      return 'factoryUsb.wizardTokenConsumed';
  }
  return 'factoryUsb.wizardErrorUnknown';
}

/** One-time code lifetime, counted down on screen: `mm:ss`, minutes unbounded. */
function formatTtl(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * Failures of handing the code to the agent. An agent that already holds a machine token says
 * nothing about the earlier code, so a rejected one is either used up or simply wrong — both are
 * answered by minting a new code, so they share one message.
 */
function codeDeliveryMessageKey(error: unknown): string {
  if (isFactoryBridgeError(error)) {
    if (error.errorCode === BRIDGE_ERROR_CODE.unavailable)
      return 'factoryUsb.wizardBridgeUnavailable';
    if (error.status === 409 || error.errorCode === BRIDGE_ERROR_CODE.tokenConsumed) {
      return 'factoryUsb.wizardAuthCodeUsed';
    }
  }
  return 'factoryUsb.wizardAuthDeliverFailed';
}

const INPUT_CLASS =
  'h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors ' +
  'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Issuing wizard. The bridge is a loopback service on the administrator's machine:
 * every step here is executed against it, and nothing is written to the registry
 * until the bridge reports `done`. The medium is issued unassigned — assigning it to a
 * person is a separate step in the registry, not part of the issue.
 */
export function FactoryUsbProvisionWizard({
  open,
  onOpenChange,
  onIssued,
}: FactoryUsbProvisionWizardProps) {
  const { t } = useTranslation();
  const form = useForm<ProvisionFormValues>({
    resolver: zodResolver(provisionFormSchema),
    defaultValues: { admin: '', supervisor: '' },
  });
  const { errors, isSubmitting } = form.formState;

  const [step, setStep] = useState<WizardStep>('auth');
  const [session, setSession] = useState<BridgeSession | null>(null);
  const [remaining, setRemaining] = useState(0);
  const [mintingCode, setMintingCode] = useState(false);
  const [deliveringCode, setDeliveringCode] = useState(false);
  const [codeDelivered, setCodeDelivered] = useState(false);
  const [authMessageKey, setAuthMessageKey] = useState<string | null>(null);
  const [checkingBridge, setCheckingBridge] = useState(false);
  const [bridgeReady, setBridgeReady] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [devices, setDevices] = useState<BridgeDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProvisionProgress | null>(null);
  const [activeStage, setActiveStage] = useState<ProvisionWriteStage | null>(null);
  const [messageKey, setMessageKey] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  const candidates = useMemo(() => selectableBridgeDevices(devices), [devices]);
  const selectedDevice = useMemo(
    () => devices.find((device) => device.id === selectedDeviceId) ?? null,
    [devices, selectedDeviceId]
  );

  const resetWizard = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStep('auth');
    setSession(null);
    setRemaining(0);
    setMintingCode(false);
    setDeliveringCode(false);
    setCodeDelivered(false);
    setAuthMessageKey(null);
    setCheckingBridge(false);
    setBridgeReady(false);
    setToken(null);
    setDevices([]);
    setDevicesLoading(false);
    setSelectedDeviceId(null);
    setProgress(null);
    setActiveStage(null);
    setMessageKey(null);
    form.reset();
  }, [form]);

  /** Opens a fresh bridge run — this call doubles as the availability probe. */
  const checkBridge = useCallback(async () => {
    setCheckingBridge(true);
    setBridgeReady(false);
    setMessageKey(null);
    try {
      const freshToken = await startBridge();
      setToken(freshToken);
      setBridgeReady(true);
    } catch (error) {
      setToken(null);
      setMessageKey(messageKeyFor(error));
    } finally {
      setCheckingBridge(false);
    }
  }, []);

  /**
   * Mints the one-time code on the License Server under the administrator's own session. Nothing
   * is written to the registry here — the code only opens the agent's way to the server.
   */
  const requestCode = useCallback(async () => {
    setMintingCode(true);
    setAuthMessageKey(null);
    setCodeDelivered(false);
    try {
      const minted = await createBridgeSession();
      setSession(minted);
      setRemaining(minted.expires_in_s);
    } catch {
      setSession(null);
      setRemaining(0);
      setAuthMessageKey('factoryUsb.wizardAuthError');
    } finally {
      setMintingCode(false);
    }
  }, []);

  /**
   * Hands the code to an agent that is already running. Opening the bridge run first makes this
   * path double as the availability probe, so a success here means both things at once: the agent
   * answers, and it has taken the code.
   */
  const deliverCode = useCallback(async () => {
    if (!session) return;
    setDeliveringCode(true);
    setAuthMessageKey(null);
    try {
      const freshToken = await startBridge();
      await authorizeBridge(freshToken, session.one_time_code);
      setToken(freshToken);
      setBridgeReady(true);
      setCodeDelivered(true);
    } catch (error) {
      setAuthMessageKey(codeDeliveryMessageKey(error));
    } finally {
      setDeliveringCode(false);
    }
  }, [session]);

  const loadDevices = useCallback(async (activeToken: string) => {
    setDevicesLoading(true);
    setMessageKey(null);
    try {
      const found = await listBridgeDevices(activeToken);
      const usable = selectableBridgeDevices(found);
      setDevices(found);
      // Fail-closed: nothing is preselected unless there is exactly one candidate.
      setSelectedDeviceId(usable.length === 1 ? (usable[0] as BridgeDevice).id : null);
    } catch (error) {
      setDevices([]);
      setSelectedDeviceId(null);
      setMessageKey(messageKeyFor(error));
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      return;
    }
    resetWizard();
  }, [open, resetWizard]);

  /**
   * The bridge step probes availability on arrival. A token is never re-fetched: the run opened
   * here is the one the issue is written in, and it stays valid across the remaining steps.
   */
  useEffect(() => {
    if (!open || step !== 'bridge' || token !== null) return;
    void checkBridge();
  }, [checkBridge, open, step, token]);

  /** Countdown of the minted code; it stops as soon as the code is handed over or the run ends. */
  useEffect(() => {
    if (!session || codeDelivered) return;
    const timer = setInterval(() => {
      setRemaining((seconds) => (seconds > 0 ? seconds - 1 : 0));
    }, 1_000);
    return () => clearInterval(timer);
  }, [codeDelivered, session]);

  useEffect(() => {
    if (!open || step !== 'devices' || !token) return;
    void loadDevices(token);
  }, [loadDevices, open, step, token]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const restart = useCallback(() => {
    resetWizard();
  }, [resetWizard]);

  const submit = form.handleSubmit(async (values) => {
    if (!token || !selectedDeviceId) return;
    const controller = new AbortController();
    abortRef.current = controller;

    setMessageKey(null);
    setProgress(null);
    setActiveStage(null);
    setStep('progress');

    try {
      const started = await startProvision(
        token,
        { device: selectedDeviceId, ...values },
        controller.signal
      );
      setProgress(started);
      if (isProvisionWriteStage(started.stage)) setActiveStage(started.stage);

      const finished = await waitForProvision(
        token,
        started.id,
        (next) => {
          setProgress(next);
          if (isProvisionWriteStage(next.stage)) setActiveStage(next.stage);
        },
        controller.signal
      );

      setProgress(finished);
      if (finished.stage === 'done') onIssued?.(finished);
    } catch (error) {
      if (isAbortError(error)) return;
      setMessageKey(messageKeyFor(error));
    }
  });

  const field = (name: keyof ProvisionFormValues, labelKey: string) => {
    const fieldError = errors[name];
    return (
      <div className="flex flex-col gap-1" key={name}>
        <label className="text-sm font-medium" htmlFor={`factory-usb-${name}`}>
          {t(labelKey)}
        </label>
        <input
          id={`factory-usb-${name}`}
          className={INPUT_CLASS}
          autoComplete="off"
          disabled={isSubmitting}
          {...form.register(name)}
        />
        {fieldError ? (
          <p className="text-xs text-destructive">
            {t(fieldError.message ?? 'factoryUsb.wizardValidationRequired', {
              max: PROVISION_FIELD_MAX[name],
            })}
          </p>
        ) : null}
      </div>
    );
  };

  const codeExpired = session !== null && remaining <= 0;
  const backStep = BACK_STEP[step];
  const activeIndex = activeStage ? provisionWriteStageIndex(activeStage) : -1;
  const isDone = progress?.stage === 'done';
  const isFailed = progress?.stage === 'failed';
  const issuedKeyId = progress?.key_id ?? progress?.id ?? '';

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) resetWizard();
        onOpenChange(next);
      }}
    >
      <SheetContent className="flex w-full flex-col gap-4 sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t('factoryUsb.wizardTitle')}</SheetTitle>
          <SheetDescription>{t('factoryUsb.wizardDescription')}</SheetDescription>
        </SheetHeader>

        <ol className="flex flex-wrap gap-2" aria-label={t('factoryUsb.wizardTitle')}>
          {STEP_ORDER.map((wizardStep) => (
            <li key={wizardStep}>
              <Badge variant={wizardStep === step ? 'default' : 'outline'}>
                {t(STEP_KEYS[wizardStep])}
              </Badge>
            </li>
          ))}
        </ol>

        <div className="flex-1 overflow-y-auto">
          {step === 'auth' ? (
            <div className="flex flex-col gap-3">
              <p className="font-medium">{t('factoryUsb.wizardAuthTitle')}</p>
              <p className="text-sm text-muted-foreground">{t('factoryUsb.wizardAuthIntro')}</p>

              {codeDelivered ? (
                <div className="flex flex-col gap-2 rounded-md border border-dashed p-4">
                  <Badge variant="default">{t('factoryUsb.wizardAuthDelivered')}</Badge>
                  <p className="text-sm text-muted-foreground">
                    {t('factoryUsb.wizardAuthDeliveredHint')}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-3 rounded-md border border-dashed p-4">
                  {session ? (
                    <div className="flex flex-col gap-1">
                      <span className="text-sm font-medium">
                        {t('factoryUsb.wizardAuthCodeLabel')}
                      </span>
                      <span className="font-mono text-2xl tracking-widest">
                        {session.one_time_code}
                      </span>
                      <span
                        className={
                          codeExpired ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'
                        }
                        role={codeExpired ? 'alert' : undefined}
                      >
                        {codeExpired
                          ? t('factoryUsb.wizardAuthExpired')
                          : t('factoryUsb.wizardAuthTtl', { ttl: formatTtl(remaining) })}
                      </span>
                      <p className="text-sm text-muted-foreground">
                        {t('factoryUsb.wizardAuthManualHint')}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('factoryUsb.wizardAuthManualHint')}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      className="self-start"
                      disabled={mintingCode || deliveringCode}
                      onClick={() => void requestCode()}
                    >
                      {mintingCode
                        ? t('factoryUsb.wizardAuthRequesting')
                        : session
                          ? t('factoryUsb.wizardAuthGetCodeAgain')
                          : t('factoryUsb.wizardAuthGetCode')}
                    </Button>
                    {session && !codeExpired ? (
                      <Button
                        className="self-start"
                        disabled={deliveringCode || mintingCode}
                        onClick={() => void deliverCode()}
                      >
                        {deliveringCode
                          ? t('factoryUsb.wizardAuthDelivering')
                          : t('factoryUsb.wizardAuthDeliver')}
                      </Button>
                    ) : null}
                  </div>
                </div>
              )}

              {authMessageKey ? (
                <p className="text-sm text-destructive" role="alert">
                  {t(authMessageKey)}
                </p>
              ) : null}
            </div>
          ) : null}

          {step === 'bridge' ? (
            <div className="flex flex-col gap-3">
              {checkingBridge ? (
                <>
                  <Skeleton className="h-4 w-48" />
                  <p className="text-sm text-muted-foreground">
                    {t('factoryUsb.wizardBridgeChecking')}
                  </p>
                </>
              ) : bridgeReady ? (
                <Badge variant="default">{t('factoryUsb.wizardBridgeReady')}</Badge>
              ) : (
                <div className="flex flex-col gap-3 rounded-md border border-dashed p-4">
                  <p className="font-medium">{t('factoryUsb.wizardBridgeUnavailable')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('factoryUsb.wizardBridgeIntro')}
                  </p>
                  <p className="font-mono text-xs text-muted-foreground">
                    {t('factoryUsb.wizardBridgeHint', { endpoint: BRIDGE_BASE_URL })}
                  </p>
                  <Button
                    variant="outline"
                    className="self-start"
                    onClick={() => void checkBridge()}
                  >
                    {t('factoryUsb.wizardBridgeRetry')}
                  </Button>
                </div>
              )}
            </div>
          ) : null}

          {step === 'devices' ? (
            <div className="flex flex-col gap-3">
              <p className="font-medium">{t('factoryUsb.wizardDevicesTitle')}</p>

              {candidates.length === 1 && !devicesLoading ? (
                <p className="text-sm text-muted-foreground">{t('factoryUsb.wizardDevicesPick')}</p>
              ) : null}

              {devicesLoading ? (
                <div className="flex flex-col gap-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : devices.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('factoryUsb.wizardDevicesEmpty')}
                </p>
              ) : (
                <ul className="flex flex-col gap-2">
                  {devices.map((device) => {
                    const isSelected = device.id === selectedDeviceId;
                    return (
                      <li key={device.id}>
                        <button
                          type="button"
                          disabled={!device.selectable}
                          aria-pressed={isSelected}
                          onClick={() => setSelectedDeviceId(device.id)}
                          className={`w-full rounded-md border p-3 text-left text-sm transition-colors ${
                            isSelected ? 'border-primary bg-accent' : 'border-input'
                          } ${device.selectable ? '' : 'cursor-not-allowed opacity-60'}`}
                        >
                          <span className="font-medium">{device.label}</span>
                          <span className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                            {device.serial ? (
                              <span>
                                {t('factoryUsb.wizardDeviceSerial', { serial: device.serial })}
                              </span>
                            ) : null}
                            {device.mount_point ? (
                              <span>
                                {t('factoryUsb.wizardDeviceMount', { mount: device.mount_point })}
                              </span>
                            ) : null}
                            {device.reason ? (
                              <span className="text-destructive">
                                {t(DEVICE_REASON_KEYS[device.reason])}
                              </span>
                            ) : null}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {!devicesLoading && candidates.length === 0 && devices.length > 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t('factoryUsb.wizardDevicesEmpty')}
                </p>
              ) : null}
              {!devicesLoading && candidates.length > 1 ? (
                <p className="text-sm text-destructive">{t('factoryUsb.wizardDevicesAmbiguous')}</p>
              ) : null}

              <Button
                variant="outline"
                size="sm"
                className="self-start"
                disabled={devicesLoading || !token}
                onClick={() => {
                  if (token) void loadDevices(token);
                }}
              >
                {t('factoryUsb.wizardDevicesRefresh')}
              </Button>

              {messageKey ? (
                <p className="text-sm text-destructive" role="alert">
                  {t(messageKey)}
                </p>
              ) : null}

              {/*
               * Both signatures sit with the medium: issuing carries no employee any more, so
               * this is the whole form — and it is signed by the same hand that picks the medium.
               */}
              <form
                className="flex flex-col gap-3 border-t pt-4"
                onSubmit={(event) => void submit(event)}
                noValidate
              >
                {field('admin', 'factoryUsb.wizardFieldAdmin')}
                {field('supervisor', 'factoryUsb.wizardFieldSupervisor')}
                {selectedDevice ? (
                  <p className="text-xs text-muted-foreground">
                    {t('factoryUsb.wizardDeviceSerial', {
                      serial: selectedDevice.serial ?? selectedDevice.label,
                    })}
                  </p>
                ) : null}
                <Button
                  type="submit"
                  className="self-start"
                  disabled={isSubmitting || !selectedDeviceId}
                >
                  {t('factoryUsb.wizardSubmit')}
                </Button>
              </form>
            </div>
          ) : null}

          {step === 'progress' ? (
            <div className="flex flex-col gap-4">
              {isDone ? (
                <div className="flex flex-col gap-2 rounded-md border border-dashed p-4">
                  <p className="font-medium">{t('factoryUsb.wizardResultTitle')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('factoryUsb.wizardResultDescription', { keyId: issuedKeyId })}
                  </p>
                </div>
              ) : isFailed ? (
                <div className="flex flex-col gap-2 rounded-md border border-destructive/40 p-4">
                  <p className="font-medium">{t('factoryUsb.wizardFailedTitle')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('factoryUsb.wizardFailedDescription')}
                  </p>
                  {progress?.reason ? (
                    <p className="font-mono text-xs text-muted-foreground">
                      {t('factoryUsb.wizardFailedReason', { reason: progress.reason })}
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('factoryUsb.wizardProgressTitle')}
                </p>
              )}

              <ol className="flex flex-col gap-2">
                {PROVISION_STAGES.map((stage, index) => {
                  const failedHere = isFailed && index === activeIndex;
                  const passed = isDone || index < activeIndex;
                  const running = !isDone && !isFailed && index === activeIndex;
                  return (
                    <li key={stage} className="flex items-center gap-3 text-sm">
                      <span
                        aria-hidden="true"
                        className={`inline-block h-2 w-2 rounded-full ${
                          failedHere
                            ? 'bg-destructive'
                            : passed
                              ? 'bg-primary'
                              : running
                                ? 'bg-primary/60'
                                : 'bg-muted'
                        }`}
                      />
                      <span
                        className={
                          failedHere
                            ? 'text-destructive'
                            : passed || running
                              ? 'text-foreground'
                              : 'text-muted-foreground'
                        }
                      >
                        {t(STAGE_LABEL_KEYS[stage])}
                      </span>
                      {running ? (
                        <span className="text-xs text-muted-foreground">
                          {t('factoryUsb.wizardProgressHint')}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ol>

              {messageKey ? (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-destructive" role="alert">
                    {t(messageKey)}
                  </p>
                  <Button variant="outline" size="sm" className="self-start" onClick={restart}>
                    {t('factoryUsb.wizardRestart')}
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <SheetFooter className="flex-row justify-between gap-2 sm:justify-between">
          {backStep ? (
            <Button variant="outline" onClick={() => setStep(backStep)}>
              {t('factoryUsb.wizardBack')}
            </Button>
          ) : (
            <span />
          )}

          {step === 'auth' ? (
            /*
             * Fail-closed step: the bridge is never reached until the agent has taken the code —
             * the local device list must not suggest that issuing can proceed unauthorized.
             */
            <Button
              disabled={mintingCode || deliveringCode || !codeDelivered}
              onClick={() => setStep('bridge')}
            >
              {t('factoryUsb.wizardNext')}
            </Button>
          ) : step === 'bridge' ? (
            <div className="flex gap-2">
              <Button
                variant="outline"
                disabled={checkingBridge}
                onClick={() => void checkBridge()}
              >
                {t('factoryUsb.wizardBridgeCheck')}
              </Button>
              <Button disabled={!bridgeReady} onClick={() => setStep('devices')}>
                {t('factoryUsb.wizardNext')}
              </Button>
            </div>
          ) : step === 'progress' ? (
            <Button
              variant={isFailed || messageKey ? 'outline' : 'default'}
              onClick={() => onOpenChange(false)}
            >
              {t('factoryUsb.wizardClose')}
            </Button>
          ) : (
            <span />
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
